<template>
  <Modal v-on="$listeners" v-bind="$attrs" :large="true">
    <template v-slot:title>
      <i class="fas fa-cog mr-2"></i> Account settings
    </template>
    <template v-slot:body>
      <div>
        <div class="card">
          <h5 class="card-header">Account</h5>
          <div class="card-body">
            <div class="row my-2">
              <div class="col-sm-4 col-md-3 label">Email address</div>
              <div class="col-sm">{{user.email}}</div>
            </div>
            <div class="row my-2">
              <div class="col-sm-4 col-md-3 label">Account status</div>
              <div class="col-sm">
                <span
                  class="badge"
                  :class="{ 'badge-success': user.emailVerified, 'badge-danger': !user.emailVerified }"
                >{{user.emailVerified ? 'Verified' : 'Not yet verified'}}</span>
                <button
                  class="btn btn-sm btn-secondary ml-2"
                  href="#"
                  @click="requestNewEmail"
                  :disabled="verificationEmailSent"
                  v-if="!user.emailVerified"
                >{{verificationEmailSent ? 'New verification email sent': 'Request new verification email'}}</button>
              </div>
            </div>
            <div class="row my-2">
              <div class="col-sm-4 col-md-3 label">Account creation date</div>
              <div class="col-sm">{{creationDate}}</div>
            </div>
          </div>
        </div>
        <div class="card mt-3">
          <h5 class="card-header">Notes</h5>
          <div class="card-body">
            <!-- <div class="row my-2">
          <div class="col-sm-4 col-md-3 label">Show wordcount</div>
          <div class="col-sm">
            <div class="custom-control custom-switch">
              <input type="checkbox" class="custom-control-input" id="customSwitch1">
              <label class="custom-control-label" for="customSwitch1"></label>
            </div>
          </div>
            </div>-->
            <div class="row my-2">
              <div class="col-sm-4 col-md-3 label">Export notes</div>
              <div class="col-sm">
                <button class="btn btn-sm btn-secondary" @click="getNotes">
                  <i class="fas fa-file-export mr-2"></i>Download notes in Markdown (.zip)
                </button>
              </div>
            </div>
            <div class="row my-2">
              <div class="col-sm-4 col-md-3 label">Delete account</div>
              <div class="col-sm">
                <button class="btn btn-sm btn-danger" @click="showDeleteModal=true">
                  <i class="fas fa-trash mr-2"></i>Delete account
                </button>
              </div>
            </div>
            <div v-if="showDeleteModal">
              <div class="alert alert-danger alert-dismissible fade show" role="alert">
                <span class="badge badge-danger">Attention!</span>
                <p class="mt-0 mb-1">
                  <strong>Deleting your account cannot be undone. All your notes will be lost forever.</strong>
                </p>
                <p class="mb-0">Are you sure you still want to delete your Contexted account?</p>
                <button type="button" class="close" aria-label="Close" @click="showDeleteModal=false">
                  <span aria-hidden="true">&times;</span>
                </button>
                <button type="button" class="btn btn-sm btn-primary my-2" @click="deleteAccount" :disabled="deletingAccount">
                  <span class="spinner-grow spinner-grow-sm mr-2" role="status" aria-hidden="true" v-if="deletingAccount"></span>
                  <span>Delete Contexted account</span>
                </button>
              </div>
            </div>
            <div class="row my-2" v-if="true">
              <div class="col-sm-4 col-md-3 label">End-to-end encryption</div>
              <div class="col-sm" v-if="!encryptionKey">
                <button class="btn btn-sm btn-secondary" @click="showEnableEncryptionDialog=true" v-if="!showEnableEncryptionDialog">
                  <i class="fas fa-key mr-2"></i>Enable encryption
                </button>
                <div class="alert alert-primary alert-dismissible fade show" role="alert" v-else>
                  <span class="badge badge-danger">Attention!</span>
                  <p
                    class="mt-0 mb-1"
                  >Enabling end-to-end requires a passphrase. This passphrase needs to be entered on all your devices. Without the passphrase, it is not possible to decrypt your notes.</p>
                  <div class="form-group">
                    <label for="encryption-passphrase">
                      <b>Passphrase</b>
                    </label>
                    <input
                      :type="showEncryptionPassphrase ? 'text': 'password'"
                      class="form-control"
                      id="encryption-passphrase"
                      placeholder="Encryption passphrase"
                      v-model="encryptionPassphrase"
                    />
                  </div>
                  <div class="form-group form-check">
                    <input type="checkbox" class="form-check-input" id="show-passphrase" v-model="showEncryptionPassphrase" />
                    <label class="form-check-label" for="show-passphrase">Show passphrase</label>
                  </div>
                  <button
                    type="button"
                    class="btn btn-sm btn-primary my-2"
                    @click="enableEncryption"
                    :disabled="enablingEncryption || encryptionPassphrase.length===0"
                  >
                    <span class="spinner-grow spinner-grow-sm mr-2" role="status" aria-hidden="true" v-if="enablingEncryption"></span>
                    <span>Encrypt notes</span>
                  </button>
                  <button type="button" class="close" aria-label="Close" @click="showEnableEncryptionDialog=false">
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
              </div>
              <div class="col-sm" v-else>
                <button class="btn btn-sm btn-secondary" @click="showDisableEncryptionDialog=true" v-if="!showDisableEncryptionDialog">
                  <i class="fas fa-key mr-2"></i>Disable encryption
                </button>
                <div class="alert alert-primary alert-dismissible fade show" role="alert" v-else>
                  <p>Enter your passphrase to disable encryption.</p>
                  <div class="form-group">
                    <label for="encryption-passphrase">
                      <b>Passphrase</b>
                    </label>
                    <input
                      :type="showEncryptionPassphrase ? 'text': 'password'"
                      class="form-control"
                      id="encryption-passphrase"
                      placeholder="Encryption passphrase"
                      v-model="encryptionPassphrase"
                    />
                  </div>
                  <button
                    type="button"
                    class="btn btn-sm btn-primary my-2"
                    @click="disableEncryption"
                    :disabled="disablingEncryption || encryptionPassphrase.length===0"
                  >
                    <span class="spinner-grow spinner-grow-sm mr-2" role="status" aria-hidden="true" v-if="disablingEncryption"></span>
                    <span>Disable encryption</span>
                  </button>
                  <button type="button" class="close" aria-label="Close" @click="showDisableEncryptionDialog=false">
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Modal>
</template>
<script>
import format from 'date-fns/format'
import JSZip from 'jszip'
import FileSaver from 'file-saver'
import Modal from '@/components/Modal'
import { signOut } from '@/utils/services'
import { trackEvent } from '@/utils/analytics'

export default {
  components: {
    Modal
  },
  computed: {
    user() {
      return this.$store.state.user
    },
    creationDate() {
      const timestamp = Date.parse(this.user.metadata.creationTime)
      return format(timestamp, 'DD/MM/YYYY')
    },
    notes() {
      return this.$store.getters.notes
    },
    encryptionKey() {
      return this.$store.state.encryption.encryptionKey
    }
  },
  data() {
    return {
      showDeleteModal: false,
      deletingAccount: false,
      verificationEmailSent: false,
      showEnableEncryptionDialog: false,
      showDisableEncryptionDialog: false,
      showEncryptionPassphrase: false,
      enablingEncryption: false,
      disablingEncryption: false,
      encryptionPassphrase: ''
    }
  },
  methods: {
    async getNotes() {
      trackEvent('export to md', {
        eventCategory: 'app',
        eventAction: 'export_md',
        eventLabel: this.user.email
      })
      const zip = new JSZip()
      this.notes.forEach(note => {
        zip.file(`${note.title}-${note.id}.md`, note.content)
      })
      const blob = await zip.generateAsync({ type: 'blob' })
      const currentDate = format(new Date(), 'YYYYMMDD')
      FileSaver.saveAs(blob, `contexted-${this.user.email}-${currentDate}.zip`)
    },
    async deleteAccount() {
      this.deletingAccount = true
      try {
        const user = this.user.email
        await this.user.delete()
        this.deletingAccount = false
        signOut()
        trackEvent('user deletes account', {
          eventCategory: 'user',
          eventAction: 'delete',
          eventLabel: user
        })
        this.$router.push({ name: 'rootNote' })
      } catch (e) {
        this.deletingAccount = true
        console.log(e)
      }
    },
    requestNewEmail() {
      this.$store.dispatch('sendVerificationEmail')
      this.verificationEmailSent = true
    },
    async enableEncryption() {
      this.enablingEncryption = true
      await this.$store.dispatch('encryption/enableEncryption', this.encryptionPassphrase)
      this.enablingEncryption = false
      console.log('encrypt the notes')
    },
    async disableEncryption() {
      this.disablingEncryption = true
      await this.$store.dispatch('encryption/disableEncryption', this.encryptionPassphrase)
      this.disablingEncryption = false
    }
  }
}
</script>
<style scoped lang="scss">
.label {
  font-weight: bold;
}
// h5 {
//   font-size: $h5-size;
//   font-weight: $h5-weight;
//   padding-bottom: 0px;
// }
</style>
